import React, { useState,useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, Button,
    Badge, Card, Row, Col, Alert,
    Stack, ListGroup,
    Form
} from 'react-bootstrap';
import {
    FiUser, FiPhone, FiDollarSign, FiCalendar, FiArchive,
    FiX, FiFileText, FiCheckCircle, FiAlertCircle,
    FiPrinter, FiHome
} from 'react-icons/fi';
import { FaRegHandshake } from 'react-icons/fa';
import { useChangeTenantDetailsPropertyMutation, useChangeUnitDetailsMutation } from '../../../lib/apiSlice/property/propertySlice.tsx';
import { toast } from 'react-toastify';

const normalizeInvoicePeriod = (value) => {
    if (!value) return "monthly";
    const normalized = value.toString().trim().toLowerCase().replace(/[_\s]/g, "-");
    if (["monthly", "month"].includes(normalized)) return "monthly";
    if (["quarterly", "quartely", "quarter"].includes(normalized)) return "quarterly";
    if (["semi-annual", "semi-annually", "semiannual", "semiannually", "semi-annualy", "semiannualy"].includes(normalized)) {
        return "semi-annual";
    }
    if (["annually", "annual", "yearly", "year"].includes(normalized)) return "annually";
    return normalized;
};

const getInvoicePeriodLabel = (value) => {
    const period = normalizeInvoicePeriod(value);
    if (period === "quarterly") return "Quarterly";
    if (period === "semi-annual") return "Semi-Annual";
    if (period === "annually") return "Annually";
    return "Monthly";
};

const getInvoicePeriodMultiplier = (value) => {
    const period = normalizeInvoicePeriod(value);
    if (period === "quarterly") return 3;
    if (period === "semi-annual") return 6;
    if (period === "annually") return 12;
    return 1;
};

const InvoiceDetailsModal = ({ show, handleClose, invoice, handleKraInvoice }) => {
    // State management
    const [invoiceType, setInvoiceType] = useState(normalizeInvoicePeriod(invoice?.type) || 'monthly');
    const [kraStatus, setKraStatus] = useState(invoice?.kra_pin && invoice.kra_pin !== 'N/A' ? 'kra' : 'non_kra');
    const [isEditingKra, setIsEditingKra] = useState(false);
    const [purchaseType, setPurchaseType] = useState(invoice?.has_purchase);
    const [showConfirmSchedule, setShowConfirmSchedule] = useState(false);
    const [showConfirmCompliance, setShowConfirmCompliance] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingPurchaseType, setPendingPurchaseType] = useState(null);
    const [kraPin, setKraPin] = useState(invoice?.kra_pin);
    const [purchaseDetails, setPurchaseDetails] = useState({
        name: invoice?.purchase_name || '',
        number: invoice?.purchase_number || ''
    });

    useEffect(() => {
        setPurchaseDetails({
            name: invoice?.purchase_name || '',
            number: invoice?.purchase_number || ''
        });
    }, [invoice]);
    useEffect(() => {
        setInvoiceType(normalizeInvoicePeriod(invoice?.type) || 'monthly');
    }, [invoice]);

    // API mutations
    const [changeDetails, { isLoading: isChangingDetails }] = useChangeUnitDetailsMutation();
    const [changeTenantDetail, { isLoading: isChangingTenant }] = useChangeTenantDetailsPropertyMutation();

    const isKRA = invoice?.kra_compliance;
    const currentSchedule = normalizeInvoicePeriod(invoice?.type) || 'monthly';
    const isScheduleChanged = currentSchedule !== invoiceType;
    const isComplianceChanged = (isKRA ? "kra" : "non_kra") !== kraStatus;

    const options = [
        { id: "monthly", label: "Monthly", badge: "Standard" },
        { id: "quarterly", label: "Quarterly", badge: null },
        { id: "semi-annual", label: "Semi-Annual", badge: null },
        { id: "annually", label: "Annually", badge: null },
    ];

    const handleToggleChange = (newValue) => {
        setPendingPurchaseType(newValue);
        setShowConfirmModal(true);
    };

    const handleChangeSchedule = async () => {
        if (!invoice?.unit_id) {
            toast.error("Missing house ID");
            return;
        }

        try {
            await changeDetails({
                houseId: invoice.unit_id,
                bodyCredentials: { generate_invoice_schedule: invoiceType }
            }).unwrap();

            setShowConfirmSchedule(false);
            handleClose();
            toast.success('Successfully changed the invoice schedule');
        } catch (err) {
            console.error("Error changing schedule:", err);
            toast.error('Failed to change schedule');
        }
    };

    const handleConfirmChange = async () => {
        try {
            if (!invoice?.unit_id) {
                toast.error("Missing house ID");
                return;
            }

            const payload = {
                has_purchase_order_number: pendingPurchaseType !== null ? pendingPurchaseType : invoice?.has_purchase,
                ...(invoice?.has_purchase && {
                    purchase_order_name: purchaseDetails.name,
                    purchase_order_number: purchaseDetails.number
                })
            };

            await changeDetails({
                houseId: invoice.unit_id,
                bodyCredentials: payload
            }).unwrap();

            setPurchaseType(pendingPurchaseType);
            setPendingPurchaseType(null);
            setShowConfirmModal(false);
            handleClose()
            window.location.reload()
            toast.success('Successfully changed the purchase order status');
        } catch (err) {
            console.error("Error changing purchase order status:", err);
            toast.error('Failed to change purchase order status');
        }
    };

    const handleKRACompliance = async () => {
        if (kraStatus === 'kra' && !kraPin) {
            toast.error('KRA PIN is required for Category A');
            return;
        }

        const data = {
            is_kra_compliance: kraStatus === 'kra',
            ...(kraStatus === 'kra' && { kra_pin: kraPin })
        };

        try {
            await changeTenantDetail({
                tenantId: invoice.tenant_id,
                bodyCredentials: data
            }).unwrap();

            setShowConfirmCompliance(false);
            setIsEditingKra(false);
            toast.success('Successfully updated tenant compliance');
        } catch (err) {
            console.error("Error changing compliance:", err);
            toast.error('Failed to update tenant compliance');
        }
    };

    // Sub-components for better organization
    const TenantInfoCard = () => (
        <Card className="shadow-sm border-0 h-100">
            <Card.Header className="d-flex align-items-center bg-light">
                <FiUser className="me-2 text-primary" />
                <h5 className="mb-0">Tenant Information</h5>
            </Card.Header>
            <Card.Body>
                <div className="d-flex align-items-center mb-3">
                    <div className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle text-primary" style={{ width: '48px', height: '48px' }}>
                        <FiUser size={20} />
                    </div>
                    <div className="ms-3">
                        <h6 className="mb-0">{invoice?.tenant_name}</h6>
                        <small className="text-muted">Tenant</small>
                    </div>
                </div>
                <ListGroup variant="flush">
                    <ListGroup.Item className="d-flex align-items-center">
                        <FiPhone className="flex-shrink-0 me-2 text-muted" />
                        <span>{invoice?.tenant_phone}</span>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex align-items-center">
                        <FiHome className="flex-shrink-0 me-2 text-muted" />
                        <span>Unit {invoice?.unit}</span>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex align-items-center">
                        <FiPrinter className="flex-shrink-0 me-2 text-muted" />
                        <span>KRA PIN {invoice?.kra_pin}</span>
                    </ListGroup.Item>
                </ListGroup>
            </Card.Body>
        </Card>
    );

    const PaymentDetailsCard = () => (
        <Card className="shadow-sm border-0 h-100">
            <Card.Header className="d-flex align-items-center bg-light">
                <FiDollarSign className="me-2 text-primary" />
                <h5 className="mb-0">Payment Details</h5>
            </Card.Header>
            <Card.Body>
                <Stack gap={3}>
                    <div className="d-flex align-items-center justify-content-between">
                        <span className="text-muted">Rent Amount:</span>
                        <span className="fw-bold">KES. {invoice?.rent_amount?.toLocaleString()}</span>
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                        <span className="text-muted">Tax Amount:</span>
                        <span className="fw-bold">KES. {invoice?.tax_amount?.toLocaleString() || "0"}</span>
                    </div>
                    <hr />
                    <div className="d-flex align-items-center justify-content-between">
                        <span className="fw-bold">Total Amount:</span>
                        <span className="text-primary fw-bold fs-5">
                            KES. {(
                                (invoice?.rent_amount || 0) *
                                getInvoicePeriodMultiplier(invoice?.type)
                            ).toLocaleString()}
                        </span>
                    </div>
                </Stack>
            </Card.Body>
        </Card>
    );

    const ScheduleOptionsCard = () => (
        <Card className="border-0">
            <Card.Header className="d-flex align-items-center bg-light">
                <FiCalendar className="me-2 text-primary" />
                <h5 className="mb-0">Invoice Schedule <span>Current - <b className='p-2 text-warning text-uppercase'>{getInvoicePeriodLabel(invoice?.type)}</b></span></h5>
            </Card.Header>
            <Card.Body className="d-flex justify-content-between">
                {options.map((option) => (
                    <div
                        key={option.id}
                        className={`p-3 text-center w-auto cursor-pointer rounded border ${invoiceType === option.id ? "border-primary shadow bg-light" : "border-secondary"}`}
                        style={{ minWidth: "50px", cursor: "pointer" }}
                        onClick={() => setInvoiceType(option.id)}
                    >
                        <h6 className="mb-1">{option.label}
                            {option.badge && <span className="bg-primary ms-2 text-white badge">{option.badge}</span>}
                        </h6>
                    </div>
                ))}
            </Card.Body>
        </Card>
    );

    const ComplianceCard = () => (
        <Card className="shadow-sm border-0">
            <Card.Header className="d-flex align-items-center bg-light">
                <FaRegHandshake className="me-2 text-primary" />
                <h5 className="mb-0">Invoice Schedule <span>Current - <b className='p-2 text-info text-uppercase'>{isKRA ? 'Category A' : 'Category C'}</b></span></h5>
            </Card.Header>
            <Card.Body>
                <div className="d-flex justify-content-between">
                    {["non_kra", "kra"].map((option) => (
                        <div
                            key={option}
                            className={`p-3 text-center w-25 cursor-pointer rounded border ${kraStatus === option ? "border-primary shadow bg-light" : "border-secondary"}`}
                            style={{ minWidth: "50px", cursor: "pointer" }}
                            onClick={() => setKraStatus(option)}
                        >
                            <h6 className="mb-1">{option === "kra" ? "Category A" : "Category C"}</h6>
                        </div>
                    ))}
                </div>

                {kraStatus === "kra" && isComplianceChanged && (
                    <Alert variant="info" className="d-flex align-items-center mt-3">
                        <FiAlertCircle className="flex-shrink-0 me-2" />
                        Switching to Category A will require adding KRA PIN details
                    </Alert>
                )}

                <div className="d-flex justify-content-end mt-4 w-100 text-center">
                    <Button
                        variant="success"
                        size="sm"
                        onClick={() => setShowConfirmCompliance(true)}
                        disabled={!isComplianceChanged}
                    >
                        Change Compliance
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );

    const PurchaseOrderCard = () => (
        <Card className="border-0">
            <Card.Header className="bg-light p-3">
                <div className="d-flex align-items-center justify-content-between mb-2 w-100">
                    <div className="d-flex align-items-center">
                        <FiArchive className="me-2 text-primary" />
                        <h5 className="mb-0">Purchase Order Number - Status: <b className={`p-2 text-uppercase ms-2 ${invoice?.has_purchase ? "text-success" : "text-secondary"}`}>
                            {invoice?.has_purchase ? "ACTIVE" : "INACTIVE"}
                        </b></h5>
                    </div>
                </div>

                <div className="d-flex justify-content-end w-100">
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted">Enable Purchase Order</span>
                        <Form.Check
                            type="switch"
                            id="purchase-toggle"
                            className="m-0"
                            checked={purchaseType || invoice?.has_purchase}
                            onChange={(e) => handleToggleChange(e.target.checked)}
                        />
                    </div>
                </div>
            </Card.Header>

            {invoice?.has_purchase && (
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId="purchaseName">
                                <p className='fw-bold'>Purchase Order Name</p>
                                <Form.Control
                                    type="text"
                                    value={purchaseDetails.name}
                                    onChange={(e) => setPurchaseDetails({
                                        ...purchaseDetails,
                                        name: e.target.value
                                    })}
                                    placeholder="Enter purchase order name"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="purchaseNumber">
                                <p className='fw-bold'>Purchase Order Number</p>
                                <Form.Control
                                    type="text"
                                    value={purchaseDetails.number}
                                    onChange={(e) => setPurchaseDetails({
                                        ...purchaseDetails,
                                        number: e.target.value
                                    })}
                                    placeholder="Enter purchase order number"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-3">
                        <Button
                            variant="primary"
                            onClick={handleConfirmChange}
                            disabled={!purchaseDetails.name || !purchaseDetails.number}
                        >
                            Save Changes
                        </Button>
                    </div>
                </Card.Body>
            )}
        </Card>
    );

    return (
        <>
            <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static" className="border-0">
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title className="d-flex align-items-center">
                        <FiFileText className="me-2" size={24} />
                        <Stack gap={0}>
                            <h4 className="mb-0">Invoice Details</h4>
                            <small className="opacity-75">Unit {invoice?.unit}</small>
                        </Stack>
                        <Badge pill bg={isKRA ? "success" : "warning"} className="d-flex align-items-center ms-3">
                            {isKRA ? (
                                <>
                                    <FiCheckCircle className="ms-2 me-2" /> Compliant
                                </>
                            ) : (
                                <>
                                    <FiAlertCircle className="ms-2 me-2" /> Non Compliant
                                </>
                            )}
                        </Badge>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="p-4">
                    <Row className="mb-4 g-4">
                        <Col md={6}>
                            <TenantInfoCard />
                        </Col>
                        <Col md={6}>
                            <PaymentDetailsCard />
                        </Col>
                    </Row>

                    <Row className="g-4">
                        <Col md={12}>
                            <ScheduleOptionsCard />
                            <div className="d-flex justify-content-end mt-4 w-100 text-center">
                                <Button
                                    variant="warning"
                                    size="sm"
                                    onClick={() => setShowConfirmSchedule(true)}
                                    disabled={!isScheduleChanged || isChangingDetails}
                                >
                                    {isChangingDetails ? 'Changing...' : 'Change Schedule'}
                                </Button>
                            </div>
                        </Col>

                        <Col md={12}>
                            <ComplianceCard />
                        </Col>

                        <Col md={12}>
                            <PurchaseOrderCard />
                        </Col>
                    </Row>
                </Modal.Body>

                <Modal.Footer className="justify-content-between bg-light">
                    <Button variant="outline-secondary" onClick={handleClose} className="d-flex align-items-center">
                        <FiX className="me-2" /> Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal for KRA Compliance Change */}
            <Modal show={showConfirmCompliance} onHide={() => setShowConfirmCompliance(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Compliance Change</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to change compliance status from <b>{invoice?.kra_pin ? "Category A " : "Category C "}</b>
                        to <b>{kraStatus === "kra" ? "Category A " : "Category C "}</b>?
                    </p>

                    {kraStatus === "kra" && (
                        <Form.Group className="mt-3">
                            <p>KRA PIN <span className="text-danger">*</span></p>
                            {invoice?.kra_pin && !isEditingKra ? (
                                <div className="d-flex align-items-center">
                                    <Form.Control
                                        type="text"
                                        value={invoice.kra_pin}
                                        readOnly
                                        className="bg-light"
                                    />
                                    <Button
                                        variant="outline-primary"
                                        className="ms-2"
                                        onClick={() => setIsEditingKra(true)}
                                    >
                                        Edit
                                    </Button>
                                </div>
                            ) : (
                                <Form.Control
                                    type="text"
                                    placeholder="Enter KRA PIN"
                                    value={kraPin}
                                    onChange={(e) => setKraPin(e.target.value)}
                                    required
                                />
                            )}
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmCompliance(false)}>Cancel</Button>
                    <Button
                        variant="primary"
                        onClick={handleKRACompliance}
                        disabled={!isComplianceChanged || (kraStatus === 'kra' && !kraPin) || isChangingTenant}
                    >
                        {isChangingTenant ? 'Updating...' : 'Yes, Change'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal for Schedule Change */}
            <Modal show={showConfirmSchedule} onHide={() => setShowConfirmSchedule(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Schedule Change</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to change the schedule from <b>{getInvoicePeriodLabel(invoice?.type)}</b> to <b>{getInvoicePeriodLabel(invoiceType)}</b>?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmSchedule(false)}>Cancel</Button>
                    <Button
                        variant="primary"
                        onClick={handleChangeSchedule}
                        disabled={isChangingDetails}
                    >
                        {isChangingDetails ? 'Changing...' : 'Yes, Change'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal for Purchase Order Change */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Change</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to {pendingPurchaseType ? 'enable' : 'disable'} the purchase order status?
                    {!pendingPurchaseType && (
                        <div className="mt-2 text-warning">
                            <small>Disabling will remove any associated purchase order information.</small>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleConfirmChange}
                        disabled={isChangingDetails}
                    >
                        {isChangingDetails ? 'Processing...' : 'Confirm'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

InvoiceDetailsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    invoice: PropTypes.object.isRequired,
    handleKraInvoice: PropTypes.func,
};

export default InvoiceDetailsModal;
